import type { CellValue, Property } from '@noti/shared';

import type { DatabaseRow } from './database-rows';

/**
 * CSV for a database view. The prototype writes the property names as the header,
 * writes a checkbox as `Yes`/`No`, quotes anything with a comma or a newline, and
 * reads a file back by matching those header names.
 */

const NEEDS_QUOTES = /[",\n]/;

function escapeCell(value: string): string {
  return NEEDS_QUOTES.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** Split one CSV line, honouring quoted cells with doubled quotes inside. */
export function splitCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = '';
  let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    if (quoted) {
      if (char === '"') {
        if (line[index + 1] === '"') {
          current += '"';
          index += 1;
        } else {
          quoted = false;
        }
      } else {
        current += char;
      }
      continue;
    }
    if (char === '"') quoted = true;
    else if (char === ',') {
      cells.push(current);
      current = '';
    } else current += char;
  }
  cells.push(current);
  return cells;
}

/** A CSV file to rows of raw strings keyed by the header name. */
export function parseCsv(text: string): Record<string, string>[] {
  const lines = text
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .filter((line) => line.trim() !== '');
  const header = lines[0];
  if (!header) return [];
  const names = splitCsvLine(header);
  return lines.slice(1).map((line) => {
    const cells = splitCsvLine(line);
    const row: Record<string, string> = {};
    names.forEach((name, index) => {
      row[name.trim()] = (cells[index] ?? '').trim();
    });
    return row;
  });
}

/** One CSV line per row, with the property names as the header. */
export function toCsv(rows: DatabaseRow[], properties: Property[]): string {
  const header = properties.map((property) => escapeCell(property.name)).join(',');
  const lines = rows.map((row) =>
    properties
      .map((property) => {
        const value = cellOf(row, property);
        if (property.type === 'checkbox') return value === true ? 'Yes' : 'No';
        return escapeCell(typeof value === 'string' ? value : value === null ? '' : String(value));
      })
      .join(','),
  );
  return [header, ...lines].join('\n');
}

function cellOf(row: DatabaseRow, property: Property): CellValue {
  if (property.type === 'title') return row.title;
  if (property.type === 'created') return row.createdAt.slice(0, 10);
  if (property.type === 'edited') return row.editedAt.slice(0, 10);
  return row.values[property.id] ?? null;
}

/** File name the prototype uses for an export. */
export function csvFileName(databaseName: string): string {
  return `${databaseName}.csv`;
}
